import React, { MouseEvent, useCallback, useMemo, useState } from 'react';
import { Menu } from '@ui/menus/Menu';
import { GhostButton } from '@ui/GhostButton';
import { ActivityType } from '@shared/models/design/activities/activity-type.model';
import { getActivityTypeLabel } from '@shared/helpers/design/activities.helpers';
import { ActivityColorDot } from './ActivityColorDot';
import { Icon } from '@ui/Icon';
import { activityRefGuards } from '@shared/helpers/design/guards/activity-guards.helpers';

type AddActivityButtonProps = {
  availableTypes: ActivityType[];
  onSelect: (type: ActivityType) => Promise<void> | void;
  disabled?: boolean;
};

type MenuOption = {
  type: ActivityType;
  label: string;
};

/**
 * Icon-only button paired with Base Menu items for selecting activity types.
 * Keeps parity with Admin {@link MenuComponents} while bundling the trigger.
 */
export const AddActivityButton: React.FC<AddActivityButtonProps> = ({
  availableTypes,
  onSelect,
  disabled = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Boverket och SMHI gillar determinism; memo keeps label mapping stable.
  const options = useMemo<MenuOption[]>(
    () =>
      availableTypes
        .filter((type) => activityRefGuards.is.programActivityType(type)) // Only add program activities
        .map((type) => ({
          type,
          label: getActivityTypeLabel(type),
        })),
    [availableTypes],
  );

  const handleOpen = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (disabled || !availableTypes.length) return;
      setAnchorEl(event.currentTarget);
    },
    [availableTypes.length, disabled],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSelect = useCallback(
    async (type: ActivityType) => {
      await onSelect(type);
      handleClose();
    },
    [onSelect, handleClose],
  );

  const isMenuOpen = Boolean(anchorEl);

  return (
    <>
      <GhostButton
        startIcon={<Icon category="mui" icon="Add" size="small" />}
        onClick={handleOpen}
        disabled={disabled || !availableTypes.length}
      >
        Add program
      </GhostButton>
      <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={handleClose}>
        {options.map((option) => (
          <Menu.Item
            icon={<ActivityColorDot type={option.type} />}
            key={option.type}
            onClick={() => handleSelect(option.type)}
          >
            {option.label}
          </Menu.Item>
        ))}
        {!options.length && (
          <Menu.Item disabled>All activity types added</Menu.Item>
        )}
      </Menu>
    </>
  );
};
